import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, MenuItem, FormControl } from '@mui/material';
import { useContext } from 'react';
import { AppDataContext } from '../../context/AppDataContext';
import { SUPPORTED_LANGUAGE_CODES, normalizeLanguage, DEFAULT_LANGUAGE } from '../../constants/Languages';
import { getFunctionalItem, setFunctionalItem } from '../../utils/functionalStorage';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const LanguageSwitcher = ({ variant = 'default', inMenu = false, sx = {} }) => {
  const { t, i18n } = useTranslation();
  
  const appDataContext = useContext(AppDataContext);
  const userPreferences = appDataContext?.userPreferences || null;
  const updateUserPreferences = appDataContext?.updateUserPreferences || null;
  
  const [lang, setLang] = React.useState(normalizeLanguage(i18n.resolvedLanguage || i18n.language) || DEFAULT_LANGUAGE);
  const hasSyncedFromBackendRef = useRef(false);

  // When user logs out, userPreferences becomes null so we allow syncing again after next login
  useEffect(() => {
    if (!userPreferences) hasSyncedFromBackendRef.current = false;
  }, [userPreferences]);

  // Sync language from backend preferences, but only when there is no local choice already stored.
  // hasSyncedFromBackendRef resets on component remount (navigation away and back), so we guard
  // against overwriting an explicit user selection by checking localStorage first.
  // Priority: localStorage (explicit user choice) > backend preferences > current i18n value.
  useEffect(() => {
    if (userPreferences && userPreferences.language && !hasSyncedFromBackendRef.current) {
      hasSyncedFromBackendRef.current = true;

      const localPref = getFunctionalItem('preferred-language');
      if (localPref && SUPPORTED_LANGUAGE_CODES.includes(localPref)) {
        // User already made an explicit local choice — keep it, just make sure state agrees.
        const current = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
        if (localPref !== current) {
          setLang(localPref);
          i18n.changeLanguage(localPref);
        } else {
          setLang(localPref);
        }
        return;
      }

      // No local preference yet — this is the first login sync from backend.
      const backendLang = normalizeLanguage(userPreferences.language);
      const current = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
      if (backendLang !== current) {
        setLang(backendLang);
        i18n.changeLanguage(backendLang);
        setFunctionalItem('preferred-language', backendLang);
      }
    }
  }, [userPreferences, i18n]);

  // Keep local state in sync with i18n when it changes externally (e.g. from another switcher instance)
  useEffect(() => {
    const handleLanguageChanged = (lng) => setLang(normalizeLanguage(lng || i18n.resolvedLanguage || i18n.language) || DEFAULT_LANGUAGE);
    i18n.on('languageChanged', handleLanguageChanged);
    return () => i18n.off('languageChanged', handleLanguageChanged);
  }, [i18n]);

  const handleChange = async (event) => {
    const raw = event.target.value;
    const newLang = normalizeLanguage(raw) || DEFAULT_LANGUAGE;
    
    // Update local state and i18n immediately
    setLang(newLang);
    i18n.changeLanguage(newLang);
    
    // Always save to localStorage for immediate persistence
    setFunctionalItem('preferred-language', newLang);
    
    // Update backend preferences if we're authenticated and not on an auth page
    const isCustomerPage = window.location.pathname.startsWith('/stores/');
    const token = localStorage.getItem(isCustomerPage ? 'customer_auth_token' : 'management_auth_token');
    const currentPath = window.location.pathname;
    const isAuthPage = currentPath.includes('/login') || 
                      currentPath.includes('/management/team-member/login') ||
                      currentPath.includes('/management/app-admin/login') ||
                      currentPath.includes('/reset-password') || 
                      currentPath.includes('/set-password');
    
    if (token && !isAuthPage && updateUserPreferences) {
      try {
        // Convert to uppercase to match backend format (PT, EN, ES)
        const backendLang = newLang.toUpperCase();
        await updateUserPreferences({ language: backendLang });
      } catch (error) {
        // Log error but don't show to user - localStorage fallback is already in place
        console.error('Failed to update user preferences:', error);
        // Don't throw the error to prevent any redirects
      }
    }
  };

  // Default styling — light surface so it stays readable on dark auth/marketing backgrounds
  const defaultSx = {
    minWidth: 120,
    mb: 2,
    '& .MuiOutlinedInput-root': {
      color: BRAND.text,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      '& fieldset': { borderColor: brandAlpha('accentDark', 0.25) },
      '&:hover fieldset': { borderColor: BRAND.accent },
      '&.Mui-focused fieldset': { borderColor: BRAND.accent },
    },
    '& .MuiSelect-icon': { color: BRAND.text },
  };

  // Compact styling for dense headers
  const compactSx = {
    minWidth: 100,
    '& .MuiOutlinedInput-root': {
      height: 36,
      fontSize: '0.875rem',
      color: BRAND.text,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      '& fieldset': {
        borderColor: brandAlpha('accentDark', 0.25),
      },
      '&:hover fieldset': { borderColor: BRAND.accent },
      '&.Mui-focused fieldset': { borderColor: BRAND.accent },
    },
    '& .MuiSelect-icon': { color: BRAND.text },
    '& .MuiInputLabel-root': {
      fontSize: '0.75rem',
      transform: 'translate(14px, 8px) scale(1)',
      '&.Mui-focused, &.MuiFormLabel-filled': {
        transform: 'translate(14px, -9px) scale(0.75)'
      }
    }
  };

  const finalSx = variant === 'compact' ? { ...compactSx, ...sx } : { ...defaultSx, ...sx };

  return (
    <FormControl size="small" sx={finalSx}>
      <Select
        id="lang-select"
        value={normalizeLanguage(lang || i18n.resolvedLanguage || i18n.language) || DEFAULT_LANGUAGE}
        onChange={handleChange}
        displayEmpty
        MenuProps={{
          disableScrollLock: true,
          ...(inMenu && { sx: { zIndex: 2100 } })
        }}
        renderValue={(selected) => {
          const s = normalizeLanguage(selected);
          if (!s) return t('language');
          return t(`languages.${s}`);
        }}
      >
        {SUPPORTED_LANGUAGE_CODES.map((code) => (
          <MenuItem key={code} value={code}>{t(`languages.${code}`)}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default LanguageSwitcher;